'use client';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { call } from '../client';
import { isApiError } from '../errors';
import { idempotencyHeader } from '../idempotency';
import { putToPresignedUrl, type UploadProgress } from '../upload';
import type {
  AvailabilitySummary,
  PublicBookingCreated,
  PublicBookingRequest,
  PublicBookingStatus,
  PublicCatalog,
  PublicContent,
  PricingQuote,
  QuoteRequest,
  Document,
  DocumentContentType,
} from '../schema';
import { useApi } from './context';
import { qk } from './keys';

export function usePublicCatalog(opts: { initialData?: PublicCatalog } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: qk.publicCatalog(),
    queryFn: ({ signal }) => call(api.GET('/v1/public/catalog', { signal })),
    staleTime: 60_000,
    ...(opts.initialData ? { initialData: opts.initialData } : {}),
  });
}

export function usePublicContent(locale: 'ar' | 'en', opts: { initialData?: PublicContent } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: qk.publicContent(locale),
    queryFn: ({ signal }) => call(api.GET('/v1/public/content', { params: { query: { locale } }, signal })),
    staleTime: 60_000,
    ...(opts.initialData ? { initialData: opts.initialData } : {}),
  });
}

export interface AvailabilityQuery {
  date: string;
  mode: 'day' | 'full';
  pickup: number;
  return?: number;
  days?: number;
}

export function useAvailability(q: AvailabilityQuery | null) {
  const api = useApi();
  return useQuery<AvailabilitySummary>({
    queryKey: qk.availability({ ...q }),
    queryFn: ({ signal }) =>
      call(api.GET('/v1/public/availability', { params: { query: q as AvailabilityQuery }, signal })),
    enabled: q !== null,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

/** The live quote: pass the debounced wizard state; null while incomplete. */
export function useQuote(req: QuoteRequest | null) {
  const api = useApi();
  return useQuery<PricingQuote>({
    queryKey: qk.quote(req),
    queryFn: ({ signal }) => call(api.POST('/v1/public/quotes', { body: req as QuoteRequest, signal })),
    enabled: req !== null,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: false,
  });
}

export function useCreatePublicBooking() {
  const api = useApi();
  return useMutation<PublicBookingCreated, Error, { body: PublicBookingRequest; idempotencyKey: string }>({
    mutationFn: ({ body, idempotencyKey }) =>
      call(api.POST('/v1/public/bookings', { body, params: { header: idempotencyHeader(idempotencyKey) } })),
  });
}

/** Retries a failed checkout (the 201's retry_path, or the done page's retry). */
export function useStartCheckout() {
  const api = useApi();
  return useMutation<PublicBookingStatus, Error, { ref: string; token: string }>({
    mutationFn: ({ ref, token }) =>
      call(
        api.POST('/v1/public/bookings/{ref}/checkout', { params: { path: { ref }, query: { t: token } } }),
      ),
  });
}

/** Polls the booking status page (the done page) every `intervalMs` until stop() says so or the link is refused. */
export function useBookingStatus(
  ref: string | null,
  token: string | null,
  opts: { intervalMs?: number; stop?: (s: PublicBookingStatus) => boolean } = {},
) {
  const api = useApi();
  const { intervalMs = 2000, stop } = opts;
  return useQuery<PublicBookingStatus>({
    queryKey: qk.bookingStatus(ref ?? ''),
    queryFn: ({ signal }) =>
      call(
        api.GET('/v1/public/bookings/{ref}/status', {
          params: { path: { ref: ref ?? '' }, query: { t: token ?? '' } },
          signal,
        }),
      ),
    enabled: Boolean(ref && token),
    refetchInterval: (q) => {
      const d = q.state.data;
      if (d && stop?.(d)) return false;
      // A wrong link or token (404) will not start working by asking again.
      const e = q.state.error;
      if (isApiError(e) && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429)
        return false;
      return intervalMs;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}

export interface PublicUploadInput {
  kind: 'civil_id' | 'licence';
  file: File;
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
}

/**
 * The wizard's ID / licence upload: asks for a presigned URL, PUTs the file
 * straight to the bucket (with progress), then has the API check and store
 * it. The returned document id goes into the booking's document_ids.
 */
export function useUploadPublicDocument() {
  const api = useApi();
  return useMutation<Document, Error, PublicUploadInput>({
    mutationFn: async ({ kind, file, onProgress, signal }) => {
      const ticket = await call(
        api.POST('/v1/public/uploads', {
          body: { kind, content_type: file.type as DocumentContentType, size_bytes: file.size },
          signal,
        }),
      );
      await putToPresignedUrl(ticket, file, { onProgress, signal });
      return call(
        api.POST('/v1/public/uploads/{id}/complete', {
          params: { path: { id: ticket.document_id } },
          signal,
        }),
      );
    },
  });
}
